import { Address } from '@ton/core';
import type { StonApiClient } from '@ston-fi/api';
import {
  SWAP_PRMD_MASTER,
  SWAP_STON_POOL,
  SWAP_STON_ROUTER,
  SWAP_TON_ASSET,
} from './config';
import {
  applySlippage,
  calculateConstantProductOutput,
  type SwapDirection,
} from './swap';

export interface DirectStonQuote {
  inputUnits: bigint;
  outputUnits: bigint;
  minOutputUnits: bigint;
  lpFeeBps: bigint;
  protocolFeeBps: bigint;
  priceImpactPercent: number;
}

export type StonPoolInfo = Awaited<ReturnType<StonApiClient['getPool']>>;

export function normalizedTonAddress(value: string): string {
  return Address.parse(value).toRawString();
}

export function validateDirectStonPool(pool: StonPoolInfo): void {
  if (normalizedTonAddress(pool.address) !== normalizedTonAddress(SWAP_STON_POOL)) {
    throw new Error('STON.fi returned an unexpected pool');
  }
  if (
    normalizedTonAddress(pool.routerAddress) !== normalizedTonAddress(SWAP_STON_ROUTER)
  ) {
    throw new Error('STON.fi returned an unexpected router');
  }

  const pair = new Set([
    normalizedTonAddress(pool.token0Address),
    normalizedTonAddress(pool.token1Address),
  ]);
  if (
    !pair.has(normalizedTonAddress(SWAP_PRMD_MASTER)) ||
    !pair.has(normalizedTonAddress(SWAP_TON_ASSET))
  ) {
    throw new Error('STON.fi pool asset pair does not match TON/PRMD');
  }
}

export function createDirectStonQuote(
  pool: StonPoolInfo,
  direction: SwapDirection,
  inputUnits: bigint,
  slippagePercent: number,
): DirectStonQuote {
  validateDirectStonPool(pool);
  const token0IsPrmd =
    normalizedTonAddress(pool.token0Address) === normalizedTonAddress(SWAP_PRMD_MASTER);
  const prmdReserve = BigInt(token0IsPrmd ? pool.reserve0 : pool.reserve1);
  const tonReserve = BigInt(token0IsPrmd ? pool.reserve1 : pool.reserve0);
  const reserveIn = direction === 'tonToPrmd' ? tonReserve : prmdReserve;
  const reserveOut = direction === 'tonToPrmd' ? prmdReserve : tonReserve;
  const lpFeeBps = BigInt(pool.lpFee);
  const protocolFeeBps = BigInt(pool.protocolFee);
  const totalFeeBps = lpFeeBps + protocolFeeBps;
  const outputUnits = calculateConstantProductOutput(
    inputUnits,
    reserveIn,
    reserveOut,
    totalFeeBps,
  );
  const inputAfterFee = (inputUnits * (10_000n - totalFeeBps)) / 10_000n;
  const spotOutput = (inputAfterFee * reserveOut) / reserveIn;
  const priceImpactPercent =
    spotOutput > 0n
      ? Number(((spotOutput - outputUnits) * 1_000_000n) / spotOutput) / 10_000
      : 0;
  const minOutputUnits = applySlippage(outputUnits, slippagePercent);
  if (outputUnits <= 0n || minOutputUnits <= 0n) {
    throw new Error('STON.fi pool cannot quote this amount safely');
  }

  return {
    inputUnits,
    outputUnits,
    minOutputUnits,
    lpFeeBps,
    protocolFeeBps,
    priceImpactPercent,
  };
}
